import * as yup from 'yup';
import { puzzleNameValidator } from 'validations/string';
import { Status } from 'shared/enums';
import { getEventsPuzzlesParticipateIn } from 'services/utility/puzzles/events';

import { strings } from '../strings';

import { isAllowedToBeInSpecialCollection } from './puzzle';

export enum EditPuzzleField {
  Name = 'Name',
  Keywords = 'keywords',
  Publisher = 'Publisher',
  Collection = 'collection',
  SecondaryCollection = 'SecondaryCollection',
  Collections = 'collections',
  PhysicalDistribution = 'physicalDistribution',
  AIGenerated = 'aiGenerated',
  Status = 'status',
  PremiumTier = 'premiumTier',
  RejectMessage = 'rejectMessage',
  ForceSetPublishDate = 'forceSetPublishDate',
}

const collectionOptionSchema = yup.object({
  label: yup.string(),
  value: yup.string().required(strings.collectionIsRequired),
});

export const collectionsFieldSchema = yup
  .array()
  .of(collectionOptionSchema)
  .required(strings.collectionIsRequired)
  .min(1, strings.collectionIsRequired)
  .test('unique-collections', 'Puzzle can not have two same categories!', (collections) => {
    if (!collections) {
      return true;
    }

    const values = collections.map((collection) => collection?.value).filter(Boolean);

    return values.length === new Set(values).size;
  });

const getCollectionValuesFromParent = (parent: Record<string, unknown>) => {
  const collections = parent[EditPuzzleField.Collections] as { value?: string }[] | undefined;

  if (collections?.length) {
    return collections.map((collection) => collection?.value).filter(Boolean) as string[];
  }

  return [
    (parent[EditPuzzleField.Collection] as { value?: string } | undefined)?.value,
    (parent[EditPuzzleField.SecondaryCollection] as { value?: string } | undefined)?.value,
  ].filter(Boolean) as string[];
};

export const editPuzzleShema = (puzzleId?: string) =>
  yup.object().shape({
    [EditPuzzleField.Name]: puzzleNameValidator(),
    [EditPuzzleField.Keywords]: yup.string().trim().required(strings.collectionKeywordsRequired),
    [EditPuzzleField.Publisher]: yup.object({
      label: yup.string(),
      value: yup.string().required(strings.collectionIsRequired),
    }),
    [EditPuzzleField.Collection]: yup
      .object({
        label: yup.string(),
        value: yup.string().required(strings.collectionIsRequired),
      })
      .required(strings.collectionIsRequired),
    [EditPuzzleField.SecondaryCollection]: yup
      .object({
        label: yup.string(),
        value: yup.string().required('Category id missing'),
      })
      .nullable(),
    [EditPuzzleField.PhysicalDistribution]: yup.boolean().required(),
    [EditPuzzleField.AIGenerated]: yup.boolean().required(),
    [EditPuzzleField.Status]: yup
      .object({
        label: yup.string(),
        value: yup.string().required(strings.collectionIsRequired),
      })
      .test('isAllowToBeInSpecialCategory', 'Not allowed category set', async (option, context) => {
        if (!puzzleId) return true;

        const optionValue = option.value as Status;

        return isAllowedToBeInSpecialCollection({
          status: optionValue,
          collections: getCollectionValuesFromParent(context.parent),
        });
      })
      .test('inActiveEvent', 'Puzzle is currently in active event', async (option, context) => {
        if (!puzzleId) return true;

        const optionValue = option.value;
        const checkStatuses: string[] = [
          Status.Deleted,
          Status.Disabled,
          Status.Pending,
          Status.Rejected,
        ];
        if (checkStatuses.includes(optionValue)) {
          const events = await getEventsPuzzlesParticipateIn(puzzleId);
          if (events.length > 0) {
            const eventsString = events.map((event) => event.event.id).join(', ');
            return context.createError({
              message: `Puzzle is part of events: ${eventsString}`,
            });
          }
        }

        return true;
      }),
    [EditPuzzleField.RejectMessage]: yup.string().when(EditPuzzleField.Status, {
      is: (status: { label: string; value: string }) => status.value === Status.Rejected,
      then: () => yup.string().required('Rejection message is required.'),
      otherwise: () => yup.string(),
    }),
  });

export const editPuzzleScreenSchema = (puzzleId?: string) =>
  editPuzzleShema(puzzleId)
    .omit([EditPuzzleField.Collection, EditPuzzleField.SecondaryCollection])
    .shape({
      [EditPuzzleField.Collections]: collectionsFieldSchema,
    });
